import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {camelCase} from 'lodash';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {FLOW_NAMES} from '@/lib/analytics/analyticsConstants';
import {
  setContentLabWebPaymentFlowId,
  setContentLabWebPaymentIds,
} from '@/utils/LtidStorage';
import translate from '@/utils/translate';
import {
  Consumable,
  ContentLabPlan,
} from '@/types/models/pricing/contentLabPlan';
import ContentLabPlanBundle from '@/types/models/pricing/contentLabPlanBundle';
import {ContentLabSubscription} from '@/types/models/pricing/contentLabSubscription';
import Duration from '@/types/models/pricing/duration';
import {PlansCollection} from '@/types/models/pricing/plansCollection';
import {SubscriptionPlan} from '@/types/models/pricing/subscriptionPlan';
import {DEFAULT_CURRENCY} from '@/views/content-studio/pricing/constants';

dayjs.extend(duration);

export const CONSUMABLE_NAME = 'Popular_pays_content_studio_video';

// The consumable type is sent as a key so it's deserialized to camelCase
export const CONSUMABLE_BALANCE_KEY = camelCase(CONSUMABLE_NAME);

export const getTokenBalance = (subscription?: ContentLabSubscription) =>
  subscription?.balance?.[CONSUMABLE_BALANCE_KEY].quantity;

export const getTokenRenewalDate = (subscription?: ContentLabSubscription) =>
  subscription?.renewAtMs ? new Date(subscription?.renewAtMs) : null;

export const getPlanTokenQuantity = (plan?: ContentLabPlan) =>
  plan?.consumables?.find(
    (c: Consumable) =>
      c.consumableType === CONSUMABLE_NAME && c.isAutoRenewing === true
  )?.quantity;

export const getPlanVariant = (subscription?: ContentLabSubscription) => {
  const bundle = subscription?.plan.quoteDetails.bundle;

  switch (bundle) {
    case 'free':
      return ContentLabPlanBundle.FREE;
    case 'studio':
      return ContentLabPlanBundle.STUDIO;
    case 'business':
      return ContentLabPlanBundle.BUSINESS;
    case 'enterprise':
      return ContentLabPlanBundle.ENTERPRISE;
    default:
      return ContentLabPlanBundle.FREE;
  }
};

export const isFreePlan = (subscription?: ContentLabSubscription) =>
  getPlanVariant(subscription) === ContentLabPlanBundle.FREE;

export function getPlanFeatures(bundle: ContentLabPlanBundle): string[] {
  if (bundle === ContentLabPlanBundle.ENTERPRISE) {
    return [
      translate('Custom number of seats, credits, brands and viewers'),
      translate('No watermark'),
      translate('Early access to new features'),
      translate('Premium support'),
      translate('Dedicated Customer Success Manager'),
    ];
  }
  return [
    translate('1 brand'),
    translate('No watermark'),
    bundle === ContentLabPlanBundle.STUDIO
      ? translate('No viewers')
      : translate('Unlimited viewers'),
    bundle === ContentLabPlanBundle.BUSINESS
      ? translate('Early access to new features')
      : '',
    translate('Basic support'),
  ].filter((str) => str !== '');
}

export function getPriceCurrency(
  currency: string | undefined,
  price: number | undefined
): string {
  const actualPrice = price || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || DEFAULT_CURRENCY,
    maximumFractionDigits: Number.isInteger(actualPrice) ? 0 : 2,
  }).format(actualPrice);
}

export function getBundleTranslatedName(bundle: ContentLabPlanBundle): string {
  switch (bundle) {
    case ContentLabPlanBundle.FREE:
      return translate('free');
    case ContentLabPlanBundle.STUDIO:
      return translate('studio');
    case ContentLabPlanBundle.BUSINESS:
      return translate('business');
    case ContentLabPlanBundle.ENTERPRISE:
      return translate('enterprise');
    default:
      return translate('free');
  }
}

function convertDuration(isoDuration: string): Duration | null {
  if (isoDuration === 'P1Y') return Duration.YEARLY;
  if (isoDuration === 'P1M') return Duration.MONTHLY;
  if (isoDuration === 'P1000Y') return Duration.ETERNITY;
  console.error(`Unsupported duration: ${isoDuration}`);
  return null;
}

// Older bundles have lower tiers than the new ones
// So we allow these users to upgrade no matter what
export const legacyBundles = [
  ContentLabPlanBundle.STUDIO,
  ContentLabPlanBundle.BUSINESS,
  ContentLabPlanBundle.FREE,
];

export function convertBundle(bundle: string): ContentLabPlanBundle | null {
  switch (bundle) {
    case 'studio':
      return ContentLabPlanBundle.STUDIO;
    case 'business':
      return ContentLabPlanBundle.BUSINESS;
    case 'free':
      return ContentLabPlanBundle.FREE;
    case 'enterprise':
      return ContentLabPlanBundle.ENTERPRISE;
    case 'pro':
      return ContentLabPlanBundle.PRO;
    case 'starter':
      return ContentLabPlanBundle.STARTER;
    default:
      console.error(`Unsupported bundle: ${bundle}`);
      return null;
  }
}

export function splitPlansByDuration(plans: SubscriptionPlan[]) {
  const yearlyPlans: SubscriptionPlan[] = [];
  const monthlyPlans: SubscriptionPlan[] = [];
  plans.forEach((plan) => {
    if (plan.duration === Duration.YEARLY) {
      yearlyPlans.push(plan);
    }
    if (plan.duration === Duration.MONTHLY) {
      monthlyPlans.push(plan);
    }
  });
  return {yearlyPlans, monthlyPlans};
}

function sortPlansByTier(plans: SubscriptionPlan[]) {
  return plans.sort((a: SubscriptionPlan, b: SubscriptionPlan) =>
    a.tier && b.tier ? b.tier - a.tier : b.tier || a.tier
  );
}

function groupPlansByBundle(plans: SubscriptionPlan[]) {
  let starterPlans: SubscriptionPlan[] = [];
  let proPlans: SubscriptionPlan[] = [];

  plans.forEach((plan: SubscriptionPlan) => {
    if (plan.bundle === ContentLabPlanBundle.STARTER) starterPlans.push(plan);
    else if (plan.bundle === ContentLabPlanBundle.PRO) proPlans.push(plan);
  });

  starterPlans = sortPlansByTier(starterPlans);
  proPlans = sortPlansByTier(proPlans);

  return {starterPlans, proPlans};
}

const TRIAL_SUFFIX = '-with-trial';

const normalizeGriffinQuoteId = (griffinQuoteId: string) => {
  return griffinQuoteId.endsWith(TRIAL_SUFFIX)
    ? griffinQuoteId.slice(0, -TRIAL_SUFFIX.length)
    : griffinQuoteId;
};

export function convertPlansDataToPlansCollections(
  plansData: ContentLabPlan[],
  activeSubscription: ContentLabPlan | undefined
): {plansCollections: PlansCollection[]; activePlan?: SubscriptionPlan} {
  const plans: SubscriptionPlan[] = [];
  const activeSubscriptionPlan = plansData.find(
    (plan) =>
      plan.griffinQuoteId ===
      normalizeGriffinQuoteId(activeSubscription?.griffinQuoteId ?? '')
  );
  let activePlan: SubscriptionPlan | undefined;

  plansData.forEach((plan: ContentLabPlan) => {
    if (plan.quoteDetails) {
      const planBundle = convertBundle(plan.quoteDetails.bundle);
      const planDuration = convertDuration(plan.duration);

      if (
        planDuration === Duration.ETERNITY ||
        planDuration === null ||
        planBundle === null
      ) {
        return;
      }
      const mappedPlan = {
        id: plan.griffinQuoteId,
        price: planDuration === Duration.YEARLY ? plan.price / 12 : plan.price,
        yearlyPrice: planDuration === Duration.YEARLY ? plan.price : null,
        currency: plan.currency,
        isDisabled:
          activeSubscriptionPlan !== undefined &&
          plan.tier > activeSubscriptionPlan.tier &&
          !legacyBundles.includes(
            convertBundle(activeSubscriptionPlan.quoteDetails.bundle) ??
              ContentLabPlanBundle.FREE
          ),
        bundle: planBundle,
        tier: plan.tier,
        duration: planDuration,
        videos: getPlanTokenQuantity(plan) ?? 0,
        editors: plan.quoteDetails.editors ?? 0,
        trialQuoteId: plan.trialQuoteId,
      };
      if (plan.griffinQuoteId === activeSubscriptionPlan?.griffinQuoteId) {
        activePlan = mappedPlan;
      }
      plans.push(mappedPlan);
    }
  });

  const {starterPlans, proPlans} = groupPlansByBundle(plans);
  const starterCollection: PlansCollection = {
    id: ContentLabPlanBundle.STARTER.valueOf(),
    bundle: ContentLabPlanBundle.STARTER,
    isPromoted: false,
    plans: starterPlans,
  };

  const proCollection: PlansCollection = {
    id: ContentLabPlanBundle.PRO.valueOf(),
    bundle: ContentLabPlanBundle.PRO,
    isPromoted: true,
    plans: proPlans,
  };

  const enterpriseCollection: PlansCollection = {
    id: ContentLabPlanBundle.ENTERPRISE.valueOf(),
    bundle: ContentLabPlanBundle.ENTERPRISE,
    isPromoted: false,
    plans: [],
  };
  return {
    plansCollections: [starterCollection, proCollection, enterpriseCollection],
    activePlan,
  };
}

export function saveSessionIdsToStorage(
  productId: string,
  processId: string
): void {
  const flow = AnalyticsService.getFlow(FLOW_NAMES.CONTENT_STUDIO.PRICING);
  setContentLabWebPaymentFlowId(flow?.flow_id);
  setContentLabWebPaymentIds({
    flowId: flow?.flow_id,
    processId,
    presentationId: flow?.screen_presentation_id,
    productId,
  });
}
